import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Grid
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { TaskList } from './TaskList';
import { TaskForm } from './TaskForm';
import { TaskDetails } from './TaskDetails';
import { TaskSummary } from './TaskSummary';
import { TabPanel } from '@/components/TabPanel';
import { Task } from '@/types';
import { taskStorage } from '../../services/taskStorage';
import { Case, CaseTask } from '../../types';

interface TaskManagementProps {
  caseData: Case;
  onUpdate: (updatedCase: Case) => void;
}

export const TaskManagement: React.FC<TaskManagementProps> = ({
  caseData,
  onUpdate
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ensure tasks array exists
  const tasks = caseData.tasks || [];

  // Filter tasks based on status if filter is active
  const filteredTasks = statusFilter
    ? tasks.filter(task => task.status === statusFilter)
    : tasks;

  const handleAddTask = () => {
    setSelectedTask(null);
    setIsFormOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  const handleViewTask = (task: Task) => {
    setViewingTask(task);
  };

  const handleSaveTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString();
      
      if (selectedTask) {
        // Update existing task
        const updatedTask = {
          ...selectedTask,
          ...taskData,
          updatedAt: now
        };

        const savedTask = taskStorage.updateTask(updatedTask);
        if (!savedTask) {
          throw new Error('Failed to update task');
        }

        const updatedTasks = tasks.map(task => 
          task.id === savedTask.id ? savedTask : task
        );

        onUpdate({
          ...caseData,
          tasks: updatedTasks.map((ut) => {
            return {
              ...ut,
              updatedAt: now,
              attachments: [],
            }
          }),
          updatedAt: now
        });
      } else {
        // Create new task
        const newTask: CaseTask = {
          ...taskData,
          id: `task-${Date.now()}`,
          interventionId: caseData.id,
          assignedTo: caseData.studentId,
          createdAt: now,
          updatedAt: now,
          comments: [],
          reminders: [],
          attachments: []
        };

        const savedTask = taskStorage.saveTask(newTask);
        if (!savedTask) {
          throw new Error('Failed to save task');
        }

        onUpdate({
          ...caseData,
          tasks: [...tasks, savedTask],
          updatedAt: now
        });
      }
      
      setIsFormOpen(false);
      setSelectedTask(null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    }
  };

  const handleDeleteTask = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        const success = taskStorage.deleteTask(taskId);
        if (!success) {
          throw new Error('Failed to delete task');
        }

        onUpdate({
          ...caseData,
          tasks: tasks.filter(t => t.id !== taskId),
          updatedAt: new Date().toISOString()
        });

        if (viewingTask?.id === taskId) {
          setViewingTask(null);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete task');
      }
    }
  };

  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        throw new Error('Task not found');
      }

      const updatedTask = {
        ...task,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      const savedTask = taskStorage.updateTask(updatedTask);
      if (!savedTask) {
        throw new Error('Failed to update task');
      }

      onUpdate({
        ...caseData,
        tasks: tasks.map(t => t.id === taskId ? savedTask : t),
        updatedAt: new Date().toISOString()
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" component="h2">
            Intervention Tasks
            <Typography 
              component="span" 
              variant="h5" 
              color="text.secondary" 
              sx={{ ml: 1, fontWeight: 'normal' }}
            >
              ({tasks.length})
            </Typography>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTask}
        >
          Add Task
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TaskSummary 
        tasks={tasks}
        onFilterByStatus={setStatusFilter}
        activeFilter={statusFilter}
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TaskList
            tasks={filteredTasks}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
            onView={handleViewTask}
          />
        </Grid>
      </Grid>

      <TaskForm
        open={isFormOpen}
        task={selectedTask}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedTask(null);
        }}
        onSave={handleSaveTask}
      />

      <TaskDetails
        open={!!viewingTask}
        task={viewingTask}
        onClose={() => setViewingTask(null)}
        onUpdate={handleUpdateTask}
      />
    </Stack>
  );
};